import React from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    background: ${({ theme }) => theme.background || '#1a1a2e'};
    color: ${({ theme }) => theme.text || '#ffffff'};
    text-align: center;
`;

const ErrorIcon = styled.div`
    font-size: 4rem;
    margin-bottom: 1rem;
`;

const ErrorTitle = styled.h1`
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text || '#ffffff'};
`;

const ErrorMessage = styled.p`
    color: ${({ theme }) => theme.textSecondary || '#888'};
    margin-bottom: 2rem;
    max-width: 500px;
`;

const RetryButton = styled.button`
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    font-size: 1rem;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
`;

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console (could also send to error reporting service)
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ error, errorInfo });
    }

    handleRetry = () => {
        // Reset error state and try again
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoHome = () => {
        // Navigate to home and reset
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorContainer>
                    <ErrorIcon>⚠️</ErrorIcon>
                    <ErrorTitle>Something went wrong</ErrorTitle>
                    <ErrorMessage>
                        We're sorry, but something unexpected happened. 
                        Please try again or return to the home page.
                    </ErrorMessage>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <RetryButton onClick={this.handleRetry}>
                            Try Again
                        </RetryButton>
                        <RetryButton 
                            onClick={this.handleGoHome}
                            style={{ background: 'transparent', border: '1px solid #6366f1' }}
                        >
                            Go Home
                        </RetryButton>
                    </div>
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '600px' }}>
                            <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                                Error Details (Development Only)
                            </summary>
                            <pre style={{ 
                                background: 'rgba(0,0,0,0.3)', 
                                padding: '1rem', 
                                borderRadius: '8px',
                                overflow: 'auto',
                                fontSize: '0.75rem'
                            }}>
                                {this.state.error.toString()}
                                {this.state.errorInfo?.componentStack}
                            </pre>
                        </details>
                    )}
                </ErrorContainer>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
